import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { CreditCard, Loader2, Zap, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { persistBookingWithRetry } from '@/utils/persistBookingWithRetry';
import { formatStripeErrorMessage } from '@/lib/stripeErrors';
import { awaitCapitalCheckoutConfirmed, deriveCapitalTripReference } from '@/lib/awaitCapitalCheckout';
import { recordProcessorMismatch, processorMismatchToast } from '@/lib/processorMismatchRecovery';
import CheckoutPaymentTrustFooter from '@/components/booking/CheckoutPaymentTrustFooter';
import { zeroCheckoutKey } from '@/lib/zeroCheckoutKey';

function isStripeConnectionError(message) {
  if (!message || typeof message !== 'string') return false;
  const lower = message.toLowerCase();
  return lower.includes('connection to stripe') || lower.includes('retried') || lower.includes('network error');
}

/** processPaymentMethod's answer when the checkout must stay disabled. */
const KEEP_LOCKED = 'keep_locked';

/** The Apple Pay / Google Pay sheet's line. Customers know the plan as Defera Installments. */
const WALLET_DEPOSIT_LABEL = 'Defera Installments — Deposit';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
    },
    invalid: {
      color: '#f87171',
      iconColor: '#f87171',
    },
  },
  hidePostalCode: true,
};

function DeferaZeroFormInner({
  totalAmountCents,
  holdFeeCents = 0,
  flightData,
  baseFareCents,
  serviceFeeCents,
  departureCity,
  arrivalCity,
  departureDate,
  returnDate,
  marketingAirline,
  cabinClass,
  tripType,
  passengers,
  onSuccess,
  onError,
  disabled,
  termsAccepted,
  paymentFrequency = 'weekly', // From Price Calculator (PaymentSchedule) above
  /** 'zero' = Defera Zero API; 'capital' = Defera Capital (Slice Pay alternative) */
  checkoutVariant = 'zero',
  /** When true (Defera Capital), use Slice Pay–style deposit + installment split (not $0-down Zero). */
  sliceInstallmentsMode = false,
  /**
   * Render ONLY the Apple Pay button — no saved cards, no CardElement, no
   * confirm button, no terms block.
   *
   * Used on Defera Capital checkout while NMI is the default processor: this
   * form mounts as a small Stripe island ABOVE `DeferaCapitalNmiCheckoutForm`
   * so a customer can choose Apple Pay (which routes the booking down the
   * Capital-on-Stripe path) instead of the NMI card form. Two processors, one
   * screen — see src/constants/applePay.js for why that's deliberate and what
   * keeps it from repeating the 5VOPA3 wrong-processor incident.
   *
   * Renders nothing at all when the wallet isn't available, so non-Apple-Pay
   * customers see exactly the NMI form they see today.
   */
  applePayOnly = false,
  /** Fires when the Apple Pay sheet opens/closes, so the parent can freeze the NMI form. */
  onApplePayActiveChange,
  depositCents = 0,
  subtotalBeforeHoldCents = 0,
  depositPercentage = 20,
  installmentCount = 0,
  installmentAmountCents = 0,
  clientPaymentSchedule = null,
  /** Faye travel insurance selection (Capital only; null when none). The
   *  server re-prices it and rejects a mismatch. */
  insurance = null,
  /** Called when the server refuses that insurance (price changed, not
   *  available): Review takes the plan off and prices it again. */
  onInsuranceRefused,
  /** Called when the server couldn't hold the Defera Miles this checkout
   *  spends (the balance changed since the page loaded): Review reloads the
   *  balance and prices the trip again. Nothing was charged. */
  onMilesRefused,
  /** Rendered at the top of the payment methods, above the cards (Review's
   *  Defera Miles balance, shown like a payment method). */
  paymentMethodsTop = null,
  /**
   * How much Defera Credit the customer chose to spend on THIS checkout, in
   * cents. `null` means "all of it", which is the default and what every
   * checkout did before 2026-09-19. The server clamps to
   * min(requested, balance), so this can only ever spend less.
   */
  deferaCreditMaxCents = null,
  /** Optional content rendered between payment methods and the confirm button */
  renderBetween = null,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  /**
   * Apple Pay / Google Pay sheet (Stripe PaymentRequest). Set only after `canMakePayment()`
   * resolves truthy, and only for Defera Capital (deposit ≥ $1). Stays `null` everywhere
   * else so the button is silently skipped on browsers/devices without a wallet card.
   */
  const [paymentRequest, setPaymentRequest] = useState(null);
  /** Prevents double-submit before React state updates (parallel create-subscription = duplicate deposits). */
  const submitLockRef = useRef(false);
  /**
   * Apple Pay's `paymentmethod` event listener is attached once when the PaymentRequest
   * is created, but it must read the latest props (deposit, schedule, terms acceptance).
   * Storing the handler in a ref and updating it on every render lets the listener call
   * the freshest closure without recreating (and re-listening on) the PaymentRequest.
   */
  const applePayHandlerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const { reserveUrl } = await import('@/lib/apiUrls');
        const { mergeSupabaseEdgeHeaders } = await import('@/lib/reserveActAs');
        const res = await fetch(reserveUrl('payment-methods'), {
          headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        const data = await res.json();
        if (cancelled) return;
        setPaymentMethods(data.payment_methods || []);
        const defaultPm = data.payment_methods?.find((p) => p.is_default);
        if (defaultPm) setSelectedCardId(defaultPm.id);
        else if (data.payment_methods?.length > 0) setSelectedCardId(data.payment_methods[0].id);
        if (!data.payment_methods?.length) setUseNewCard(true);
      } catch (e) {
        if (!cancelled) setPaymentMethods([]);
      } finally {
        if (!cancelled) setLoadingMethods(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Shared post-SetupIntent path used by BOTH the CardElement and Apple Pay handlers.
   * Once we have a `paymentMethodId` (regardless of whether it came from a new card,
   * a saved card, or an Apple Pay/Google Pay token), the rest of the checkout flow
   * is identical: POST create-subscription, handle 402 (3DS), persist the booking,
   * and redirect to the dashboard. Keeping this in one function guarantees the new
   * Apple Pay path is byte-for-byte identical to the existing card path downstream.
   *
   * `onChargeOutcome` (optional): a hook that fires the moment we know whether the
   * deposit charge succeeded or failed, BEFORE any toast or redirect. Apple Pay uses
   * this to call `ev.complete('success' | 'fail')` so the wallet sheet closes with the
   * correct icon — previously we always called `ev.complete('success')` right after
   * the SetupIntent, which left the user staring at a green checkmark in Apple Pay
   * even when the deposit invoice subsequently failed (e.g. insufficient funds).
   * The card path passes no callback and behavior is unchanged.
   */
  /**
   * Resolves to `KEEP_LOCKED` when the checkout must stay disabled: the page is
   * leaving (success redirect, status page) or money may have moved and the
   * booking is still being saved. Anything else is a definitive failure, and
   * the caller hands the button back.
   */
  const processPaymentMethod = useCallback(async ({ paymentMethodId, session, capitalIdempotencyKey, zeroIdempotencyKey, onChargeOutcome }) => {
    /** Fires `onChargeOutcome` exactly once. Subsequent calls are no-ops so the Apple
     *  Pay sheet is never closed twice (Stripe throws if you call ev.complete twice). */
    let outcomeFired = false;
    const notifyOutcome = async (succeeded) => {
      if (outcomeFired) return;
      outcomeFired = true;
      if (typeof onChargeOutcome !== 'function') return;
      try {
        await onChargeOutcome(succeeded);
      } catch (e) {
        console.warn('processPaymentMethod: onChargeOutcome threw (non-fatal)', e);
      }
    };

    /**
     * A card decline is a TERMINAL outcome: Stripe definitively rejected the charge and
     * no money moved, so the cached Stripe idempotency key must be dropped immediately.
     * Customers almost always retry with a *different* card after seeing a decline;
     * reusing the old key with new parameters makes Stripe reject the retry with
     * `idempotency_error`, which the customer saw as the generic "refresh the page and
     * try again" toast instead of the real decline reason for their second card.
     * Ambiguous failures (poll timeouts, "contact support" 402s) intentionally KEEP the
     * key — there the charge may have landed and a replayed key is what protects the
     * customer from a double deposit.
     */
    const clearCapitalIdemKeyAfterDecline = () => {
      if (checkoutVariant !== 'capital') return;
      try {
        const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
        sessionStorage.removeItem(`defera_capital_idem:${session.user.id}:${flightKey}`);
      } catch { /* ignore */ }
    };

    const { deferaZeroUrl, deferaCapitalUrl } = await import('@/lib/apiUrls');
    const apiUrl = checkoutVariant === 'capital' ? deferaCapitalUrl : deferaZeroUrl;
    const createBody = {
      // Omitted entirely when they haven't narrowed it, so the request looks
      // exactly as it always has and the server's "no cap" path is the default.
      ...(Number.isFinite(deferaCreditMaxCents)
        ? { defera_credit_max_cents: Math.max(0, Math.round(deferaCreditMaxCents)) }
        : {}),
      flight_data: flightData,
      total_amount_cents: totalAmountCents,
      hold_fee_cents: holdFeeCents,
      base_fare_cents: baseFareCents,
      service_fee_cents: serviceFeeCents,
      payment_frequency: paymentFrequency,
      payment_method_id: paymentMethodId,
      departure_city: departureCity,
      arrival_city: arrivalCity,
      departure_date: departureDate,
      return_date: returnDate || null,
      marketing_airline: marketingAirline,
      cabin_class: cabinClass,
      trip_type: tripType,
      passengers: passengers,
      ...(checkoutVariant === 'capital'
        ? {
            slice_installments_mode: true,
            deposit_cents: depositCents,
            subtotal_before_hold_cents: subtotalBeforeHoldCents,
            deposit_percentage: depositPercentage,
            installment_count: installmentCount,
            installment_amount_cents: installmentAmountCents,
            client_payment_schedule: clientPaymentSchedule,
            ...(insurance ? { insurance } : {}),
            ...(capitalIdempotencyKey ? { idempotency_key: capitalIdempotencyKey } : {}),
          }
        : (zeroIdempotencyKey ? { idempotency_key: zeroIdempotencyKey } : {})),
    };

    const res = await fetch(apiUrl('create-subscription'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(createBody),
    });

    let data = await res.json();

    if (res.status === 402 && data.requires_payment && data.client_secret) {
      if (!stripe) {
        await notifyOutcome(false);
        toast.error('Payment system not ready');
        onError?.(new Error('Stripe not ready'));
        return;
      }
      /** Apple Pay 3DS is rare (the wallet already authenticated the user) but possible.
       *  We close the wallet sheet with success here — the SetupIntent did succeed and
       *  the wallet has handed control off — and any 3DS challenge then surfaces in the
       *  app via Stripe's standard modal. Closing 'fail' here would confuse users whose
       *  payment is genuinely about to succeed via 3DS. */
      await notifyOutcome(true);
      const { error: payErr, paymentIntent } = await stripe.confirmCardPayment(data.client_secret);
      if (payErr) {
        if (payErr.type === 'card_error' || payErr.decline_code || payErr.code) {
          clearCapitalIdemKeyAfterDecline();
        }
        /** Connection-error branch checks the raw SDK message text and fires first; otherwise we
         *  prefer the friendly mapping of decline_code → human reason (insufficient_funds, lost_card, …). */
        const msg = isStripeConnectionError(payErr.message)
          ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
          : (formatStripeErrorMessage(payErr) || payErr.message);
        toast.error(msg);
        onError?.(payErr);
        return;
      }
      const st = paymentIntent?.status;
      if (st !== 'succeeded' && st !== 'processing') {
        toast.error('Payment was not completed. Your trip was not confirmed.');
        onError?.(new Error('Payment incomplete'));
        return;
      }
      const finRes = await fetch(apiUrl('create-subscription'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          finalize_checkout: true,
          subscription_id: data.subscription_id,
          ...createBody,
        }),
      });
      data = await finRes.json();
      if (!finRes.ok || !data.success) {
        const rawMsg = data.detail ? `${data.error}: ${data.detail}` : (data.error || 'Could not complete booking after payment');
        const msg = isStripeConnectionError(rawMsg)
          ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
          : (formatStripeErrorMessage(data) || rawMsg);
        toast.error(msg);
        onError?.(new Error(rawMsg));
        // The card payment already went through: another click would start over.
        return KEEP_LOCKED;
      }
    } else if (!res.ok) {
      /**
       * Server-side processor guard rejected this call — the platform is on NMI
       * (or this trip should be on NMI) but the customer's tab still had the
       * Stripe form rendered. We saw this in the 5VOPA3 incident: stale React
       * state from a long-lived tab silently routed money through the wrong
       * track. Reload the page so the freshly-mounted hook fetches the current
       * processor and renders the correct form. No charge was attempted.
       *
       * `recordProcessorMismatch` enforces a circuit breaker: after the second
       * mismatch in 60s on the same trip we stop auto-reloading and show a
       * manual recovery message instead, so a misconfigured environment can't
       * trap the customer in an infinite refresh loop.
       */
      if (
        res.status === 409 &&
        data?.code === 'PROCESSOR_MISMATCH' &&
        checkoutVariant === 'capital'
      ) {
        const tripRef = deriveCapitalTripReference(flightData?.id || flightData?.offer_id) || 'pre_trip';
        const { shouldAutoReload } = recordProcessorMismatch(tripRef);
        const { message, description } = processorMismatchToast(shouldAutoReload);
        await notifyOutcome(false);
        toast.error(message, description ? { description } : undefined);
        if (shouldAutoReload) {
          setTimeout(() => window.location.reload(), 1200);
        }
        onError?.(new Error('Processor mismatch'));
        return;
      }
      /**
       * Idempotency-key collision (Stripe rejected the call BEFORE any charge).
       *
       * Happens when the customer's first attempt got far enough for the server to
       * lock parameters under the sessionStorage-derived Idempotency-Key, then they
       * changed something (different card, deposit %, recalculated total) and clicked
       * Confirm again. The server returns 409 + `error_type: 'idempotency_error'`.
       *
       * Recovery: drop the cached idempotency key so the next click gets a fresh UUID,
       * surface a friendly "refresh and try again" toast, and SKIP the checkout-status
       * poll below — by definition no webhook will fire for a request that never
       * touched Stripe's billing pipeline. Failing to early-return here would make
       * the customer stare at a 30s spinner that can only ever end with `not_found`.
       */
      if (checkoutVariant === 'capital' && data?.error_type === 'idempotency_error') {
        try {
          const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
          sessionStorage.removeItem(`defera_capital_idem:${session.user.id}:${flightKey}`);
        } catch { /* ignore */ }
        await notifyOutcome(false);
        toast.error(data.error || 'Please refresh the page and try again — your card was not charged.');
        onError?.(new Error('idempotency_error'));
        return;
      }
      /**
       * Defera Zero: another request for this same plan is already being
       * confirmed (a second tab, a replayed click). Nothing new was started;
       * keep the button off so this click can't race it.
       */
      if (data?.error_type === 'checkout_in_progress') {
        await notifyOutcome(false);
        toast.info(data.error || 'We’re already confirming this trip. Please check My Trips in a minute.');
        onError?.(new Error('checkout_in_progress'));
        return KEEP_LOCKED;
      }
      /**
       * Checkout integrity, on both variants: the server refused the plan or
       * its price before touching Stripe or saving anything
       * (_shared/capital-checkout-integrity.js). Nothing was charged, so skip
       * the poll below: it could only end in `not_found`, or find an earlier
       * attempt's pending row and send the customer to the status page. A
       * fresh Capital key lets the refreshed attempt start clean (Zero keeps none).
       */
      if (data?.error_type === 'checkout_integrity') {
        clearCapitalIdemKeyAfterDecline();
        await notifyOutcome(false);
        toast.error(data.error || 'We couldn’t confirm the price for this trip. Please refresh the page and try again.');
        onError?.(new Error('checkout_integrity'));
        return;
      }
      /**
       * The deposit failed on our side before it was ever charged (no invoice,
       * or only a draft that was then deleted), and any Defera Credit or Miles
       * it held were handed back. The server says `deposit_not_charged` only
       * then. No poll: nothing will ever land, and the poll's "still
       * confirming" ending sent QGPQCT (2026-10-07) to the status page to wait
       * on a booking that had already failed. A fresh key lets the retry start
       * clean, since the old one's Stripe calls point at the deleted draft.
       */
      if (checkoutVariant === 'capital' && data?.error_type === 'deposit_not_charged') {
        clearCapitalIdemKeyAfterDecline();
        await notifyOutcome(false);
        toast.error(data.error || 'This booking didn’t go through, and your card wasn’t charged. Please try again.');
        onError?.(new Error('deposit_not_charged'));
        return;
      }
      /**
       * The server refused the travel insurance before anything was saved or
       * charged (its price changed, or it isn't available). Same reasoning as
       * above: no poll. Review takes the plan off so the customer can add it
       * again at a fresh price, or carry on without it.
       */
      if (checkoutVariant === 'capital' && typeof data?.error_type === 'string' && data.error_type.startsWith('insurance_')) {
        clearCapitalIdemKeyAfterDecline();
        await notifyOutcome(false);
        toast.error(data.error || 'We couldn’t add the travel insurance. Please review it and try again.', {
          description: 'We took the plan off your trip. Add it again to see today’s price, or continue without it.',
        });
        onInsuranceRefused?.(data);
        onError?.(new Error(data.error_type));
        return;
      }
      /**
       * Defera Miles: the balance changed between this page and the payment
       * (spent on a gift card in another tab, say), so the server refused
       * before charging anything. No poll, for the same reason as above;
       * Review reloads the balance and re-prices the trip.
       */
      if (data?.error_type === 'miles_unavailable') {
        clearCapitalIdemKeyAfterDecline();
        await notifyOutcome(false);
        toast.error(data.error || 'Your Miles balance changed since this page loaded. Please refresh and try again.');
        onMilesRefused?.(data);
        onError?.(new Error('miles_unavailable'));
        return;
      }
      /**
       * Ambiguous failure recovery (PXYEMK / PY2RE8 class).
       *
       * The backend returned non-2xx, but for Defera Capital this category of failure
       * is *known* to sometimes happen AFTER Stripe has already charged the deposit:
       * legacy assertion bugs, replica-lag edge cases, our own friendly "if your card
       * was charged, contact support" 402, and (rarely) network/proxy errors that
       * truncate a successful response. The Stripe webhook always backfills the
       * booking from `defera_capital_pending_checkouts` within seconds, so before we
       * tell the customer their checkout failed, we poll the new
       * `defera-capital/checkout-status` endpoint to ask the server-of-record:
       * "did the booking land via the webhook?". If yes — redirect as a normal
       * success and the customer never sees the false-negative error.
       *
       * Card declines (StripeApiError forwarding gives us code/decline_code) skip the
       * poll because Stripe definitively rejected the charge — there will never be a
       * webhook to backfill a booking that doesn't exist.
       */
      const looksLikeCardDecline =
        typeof data?.code === 'string'
        || typeof data?.decline_code === 'string'
        || data?.error_type === 'card_error'
        || data?.type === 'card_error';
      /** Declines are terminal and un-charged: free the idempotency key NOW so the
       *  customer's next attempt (almost always a different card) gets a fresh key
       *  instead of a 409 "refresh the page and try again" collision. */
      if (looksLikeCardDecline) {
        clearCapitalIdemKeyAfterDecline();
      }
      if (checkoutVariant === 'capital') {
        const tripRef = deriveCapitalTripReference(flightData?.id || flightData?.offer_id);
        if (!looksLikeCardDecline && tripRef && session?.access_token) {
          /** Tell the user we're checking — never claim failure before we know. */
          toast.info('Your payment is processing — confirming with our system…', { duration: 5000 });
          try {
            const result = await awaitCapitalCheckoutConfirmed({
              tripReference: tripRef,
              accessToken: session.access_token,
              options: {
                /** Slightly tighter than the helper default so we don't stall the spinner forever. */
                timeoutMs: 30_000,
              },
            });
            if (result.status === 'confirmed') {
              /** Webhook recovered the booking. Treat exactly as success path. */
              await notifyOutcome(true);
              toast.success("Your trip is confirmed — taking you to My Trips.", { duration: 8000 });
              onSuccess?.({ success: true, recovered: true, trip_reference: tripRef });
              if (capitalIdempotencyKey) {
                try {
                  const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
                  sessionStorage.removeItem(`defera_capital_idem:${session.user.id}:${flightKey}`);
                } catch { /* ignore */ }
              }
              window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
              return KEEP_LOCKED;
            }
            if (result.status === 'processing') {
              /** Webhook hasn't created the booking yet but Stripe DID charge the
               *  deposit — tell the customer to wait + watch their dashboard. */
              await notifyOutcome(true);
              toast.success(
                `Your deposit was received. We're finalizing your trip now — it should appear in My Trips within a minute. Reference: ${tripRef}`,
                { duration: 25000 },
              );
              onSuccess?.({ success: true, defer_booking_to_webhook: true, trip_reference: tripRef });
              window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
              return KEEP_LOCKED;
            }
            if (result.status === 'pending' || result.status === 'timeout') {
              /** We polled the full deadline but never got a definitive answer. The deposit
               *  may STILL be in flight — Stripe webhook latency is occasionally minutes.
               *  Send the customer to the dedicated status page so they can keep checking
               *  even after closing this tab, and reach support with their reference. */
              await notifyOutcome(false);
              toast.info(
                `We're still confirming your payment. We'll keep checking — reference ${tripRef}.`,
                { duration: 12000 },
              );
              onError?.(new Error('Checkout still pending after poll deadline'));
              window.location.href = `${createPageUrl('CapitalCheckoutStatus')}?ref=${encodeURIComponent(tripRef)}`;
              return KEEP_LOCKED;
            }
            /** 'not_found' or other terminal non-success — fall through to the standard
             *  error toast below. Either no checkout was ever recorded server-side or the
             *  deposit was never attempted. The safe message tells the customer what to do. */
          } catch (pollErr) {
            console.warn('checkout-status poll threw, falling back to error toast', pollErr);
          }
        }
      }
      await notifyOutcome(false);
      const rawMsg = data.detail ? `${data.error}: ${data.detail}` : (data.error || 'Failed to create subscription');
      const msg = isStripeConnectionError(rawMsg)
        ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
        : (formatStripeErrorMessage(data) || rawMsg);
      toast.error(msg);
      onError?.(new Error(msg));
      return;
    }

    if (!data.success) {
      await notifyOutcome(false);
      const rawMsg = data.detail ? `${data.error}: ${data.detail}` : (data.error || 'Failed to create subscription');
      const msg = isStripeConnectionError(rawMsg)
        ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
        : (formatStripeErrorMessage(data) || rawMsg);
      toast.error(msg);
      onError?.(new Error(msg));
      return;
    }

    /** Defera Capital: deposit charged here; installment subscription is finalized by Stripe webhook (retriable). */
    if (data.defer_booking_to_webhook && checkoutVariant === 'capital') {
      await notifyOutcome(true);
      toast.success(
        `Your deposit was received. We're confirming your trip now — it should appear in My Trips within a minute.`,
        { duration: 14000 },
      );
      onSuccess?.(data);
      if (capitalIdempotencyKey) {
        try {
          const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
          sessionStorage.removeItem(`defera_capital_idem:${session.user.id}:${flightKey}`);
        } catch {
          /* ignore */
        }
      }
      window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
      return KEEP_LOCKED;
    }

    // Booking row must exist before we redirect — otherwise customers paid in Stripe but never see success.
    // Safety net: if either the payload is missing or the client-side insert fails, the
    // stripe-webhook (supabase/functions/stripe-webhook) will backfill the booking from
    // defera_capital_pending_checkouts within seconds of Stripe confirming the charge.
    // We still toast + log so customers know to watch for it, but we do NOT tell them the
    // booking failed — that's misleading now that the webhook reconciles automatically.
    const payload = data.booking_payload;
    if (!payload || typeof payload.user_id !== 'string') {
      /** Card was charged (data.success===true). For Apple Pay UX this is a payment
       *  success even though the booking row needs webhook recovery. */
      await notifyOutcome(true);
      toast.error(
        `Your payment was received. We're finalizing your trip now — it should appear in My Trips within a few minutes. If it doesn't, send us a message at defera.app/contact with reference ${data?.trip_reference || 'your trip'}.`,
        { duration: 20000 },
      );
      onError?.(new Error('Missing booking_payload'));
      return KEEP_LOCKED;
    }

    /** From here on the deposit was definitively charged. Notify before persistBookingWithRetry
     *  so the wallet sheet closes immediately with a success icon — booking persistence is a
     *  client-side write to Supabase that has its own retry layer + webhook recovery. */
    await notifyOutcome(true);

    const bookingResult = await persistBookingWithRetry(session.access_token, payload);
    if (!bookingResult.ok) {
      toast.error(
        `Your payment was received. We're finalizing your trip now — it should appear in My Trips within a few minutes. If it doesn't, send us a message at defera.app/contact with reference ${payload.trip_reference || payload.booking_reference || 'your trip'}.`,
        { duration: 25000 },
      );
      onError?.(new Error(bookingResult.error));
      return KEEP_LOCKED;
    }

    if (checkoutVariant === 'capital' && capitalIdempotencyKey) {
      try {
        const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
        sessionStorage.removeItem(`defera_capital_idem:${session.user.id}:${flightKey}`);
      } catch {
        /* ignore */
      }
    }

    onSuccess?.(data);
    const successParam = checkoutVariant === 'capital' ? 'defera_capital=success' : 'defera_zero=success';
    window.location.href = createPageUrl('Dashboard') + `?${successParam}`;
    return KEEP_LOCKED;
  }, [
    stripe,
    paymentFrequency,
    totalAmountCents,
    holdFeeCents,
    flightData,
    baseFareCents,
    serviceFeeCents,
    departureCity,
    arrivalCity,
    departureDate,
    returnDate,
    marketingAirline,
    cabinClass,
    tripType,
    passengers,
    onSuccess,
    onError,
    checkoutVariant,
    depositCents,
    subtotalBeforeHoldCents,
    depositPercentage,
    installmentCount,
    installmentAmountCents,
    clientPaymentSchedule,
    insurance,
    onInsuranceRefused,
    onMilesRefused,
  ]);

  /**
   * CardElement + saved-card path. Validates inputs, resolves a `paymentMethodId`
   * (either selected saved card or a new SetupIntent-confirmed CardElement), then
   * hands off to `processPaymentMethod` for the rest. Behavior preserved exactly.
   */
  const handleConfirm = useCallback(async () => {
    /** Sync guard first — blocks rapid multi-clicks before React re-renders `isProcessing`. */
    if (submitLockRef.current) return;
    if (!termsAccepted || isProcessing || disabled) return;
    if (!useNewCard && !selectedCardId && paymentMethods.length > 0) {
      toast.error('Please select a payment method');
      return;
    }
    if (useNewCard && (!stripe || !elements)) {
      toast.error('Payment system not ready');
      return;
    }

    flushSync(() => {
      submitLockRef.current = true;
      setIsProcessing(true);
    });

    /** Set when the page is leaving or money may have moved: the button stays off. */
    let keepLocked = false;
    try {
      if (checkoutVariant === 'capital') {
        if (depositCents < 0) {
          toast.error('Invalid deposit amount. Please refresh and try again.');
          return;
        }
        // Slice-style schedule from Review (deposit % + plan) — required when there is a real upfront ($1+).
        if (depositCents >= 100) {
          if (!installmentCount || installmentCount < 1 || !installmentAmountCents) {
            toast.error('Payment schedule is not ready. Please confirm your deposit and plan above, or pick another departure date.');
            return;
          }
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to continue');
        onError?.();
        return;
      }

      let capitalIdempotencyKey = null;
      if (checkoutVariant === 'capital') {
        try {
          const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
          const storageKey = `defera_capital_idem:${session.user.id}:${flightKey}`;
          capitalIdempotencyKey = sessionStorage.getItem(storageKey);
          if (!capitalIdempotencyKey) {
            capitalIdempotencyKey = crypto.randomUUID();
            sessionStorage.setItem(storageKey, capitalIdempotencyKey);
          }
        } catch {
          capitalIdempotencyKey = crypto.randomUUID();
        }
      }
      const zeroIdempotencyKey = checkoutVariant === 'capital' ? null : zeroCheckoutKey(session.user.id, flightData);

      let paymentMethodId = useNewCard ? null : selectedCardId;
      if (useNewCard) {
        const { deferaZeroUrl, deferaCapitalUrl } = await import('@/lib/apiUrls');
        const apiUrl = checkoutVariant === 'capital' ? deferaCapitalUrl : deferaZeroUrl;
        const setupRes = await fetch(apiUrl('create-initial-setup-intent'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        const setupData = await setupRes.json();
        if (!setupData.success || !setupData.client_secret) {
          const rawMsg = setupData.error || 'Failed to set up payment';
          const msg = isStripeConnectionError(rawMsg)
            ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
            : (formatStripeErrorMessage(setupData) || rawMsg);
          toast.error(msg);
          onError?.();
          return;
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          toast.error('Please enter your card details');
          onError?.();
          return;
        }
        const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(setupData.client_secret, {
          payment_method: { card: cardElement },
        });
        if (confirmError) {
          const msg = isStripeConnectionError(confirmError.message)
            ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
            : (formatStripeErrorMessage(confirmError) || confirmError.message);
          toast.error(msg);
          onError?.(confirmError);
          return;
        }
        paymentMethodId = setupIntent?.payment_method;
      }

      keepLocked = (await processPaymentMethod({ paymentMethodId, session, capitalIdempotencyKey, zeroIdempotencyKey })) === KEEP_LOCKED;
    } catch (err) {
      console.error('Defera checkout error:', err);
      const rawMsg = err.message || 'Something went wrong';
      const msg = isStripeConnectionError(rawMsg)
        ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
        : (formatStripeErrorMessage(err) || rawMsg);
      toast.error(msg);
      onError?.(err);
    } finally {
      if (!keepLocked) {
        submitLockRef.current = false;
        setIsProcessing(false);
      }
    }
  }, [
    termsAccepted,
    isProcessing,
    disabled,
    useNewCard,
    selectedCardId,
    paymentMethods.length,
    stripe,
    elements,
    flightData,
    onError,
    checkoutVariant,
    depositCents,
    installmentCount,
    installmentAmountCents,
    processPaymentMethod,
  ]);

  /**
   * Apple Pay / Google Pay handler. Confirms the user's wallet token against the same
   * SetupIntent the CardElement path uses, then delegates to `processPaymentMethod` so
   * downstream behavior (create-subscription → 402 retry → booking persist → redirect)
   * is identical to a card payment. Always called by the PaymentRequest event listener
   * via `applePayHandlerRef` so it sees the latest props without re-attaching.
   */
  applePayHandlerRef.current = async (ev) => {
    if (submitLockRef.current) {
      ev.complete('fail');
      return;
    }
    if (!termsAccepted || disabled) {
      ev.complete('fail');
      toast.error('Please accept the terms before continuing');
      return;
    }
    if (checkoutVariant !== 'capital') {
      // Defensive: PaymentRequest is only created for Capital, but guard anyway.
      ev.complete('fail');
      return;
    }
    // Capital deposit + plan must be ready (mirrors handleConfirm's pre-flight checks).
    if (depositCents < 100 || !installmentCount || !installmentAmountCents) {
      ev.complete('fail');
      toast.error('Payment schedule is not ready. Please confirm your deposit and plan above, or pick another departure date.');
      return;
    }

    flushSync(() => {
      submitLockRef.current = true;
      setIsProcessing(true);
    });
    // Freeze the sibling NMI form for the duration. Two processors are live on
    // this screen and only one may take the booking — see 5VOPA3.
    onApplePayActiveChange?.(true);

    /** Tracks whether we've already called `ev.complete()` so we don't double-call in the catch. */
    let sheetClosed = false;
    /** Set when the page is leaving or money may have moved: the button stays off. */
    let keepLocked = false;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        ev.complete('fail'); sheetClosed = true;
        toast.error('Please sign in to continue');
        onError?.();
        return;
      }

      let capitalIdempotencyKey = null;
      try {
        const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
        const storageKey = `defera_capital_idem:${session.user.id}:${flightKey}`;
        capitalIdempotencyKey = sessionStorage.getItem(storageKey);
        if (!capitalIdempotencyKey) {
          capitalIdempotencyKey = crypto.randomUUID();
          sessionStorage.setItem(storageKey, capitalIdempotencyKey);
        }
      } catch {
        capitalIdempotencyKey = crypto.randomUUID();
      }

      const { deferaCapitalUrl } = await import('@/lib/apiUrls');
      const { mergeSupabaseEdgeHeaders: _mergeEdge } = await import('@/lib/reserveActAs');
      const setupRes = await fetch(deferaCapitalUrl('create-initial-setup-intent'), {
        method: 'POST',
        headers: _mergeEdge({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
      });
      const setupData = await setupRes.json();
      if (!setupData.success || !setupData.client_secret) {
        ev.complete('fail'); sheetClosed = true;
        const rawMsg = setupData.error || 'Failed to set up payment';
        const msg = isStripeConnectionError(rawMsg)
          ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
          : (formatStripeErrorMessage(setupData) || rawMsg);
        toast.error(msg);
        onError?.();
        return;
      }

      // `handleActions: false` keeps Stripe.js from popping a redirect/3DS modal mid-sheet;
      // any required action surfaces as an error and we close the sheet with 'fail'.
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        setupData.client_secret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      );
      if (confirmError) {
        ev.complete('fail'); sheetClosed = true;
        const msg = isStripeConnectionError(confirmError.message)
          ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
          : (formatStripeErrorMessage(confirmError) || confirmError.message);
        toast.error(msg);
        onError?.(confirmError);
        return;
      }

      /**
       * Hand `processPaymentMethod` an outcome callback so the Apple Pay sheet closes
       * with the icon that matches what actually happened. The callback fires AS SOON
       * AS the deposit charge succeeds or fails (well within Apple Pay's 30s window for
       * `ev.complete()`).
       *
       * Why we don't call ev.complete('success') here pre-emptively: the SetupIntent
       * just attaches the card; the real money movement happens inside
       * `processPaymentMethod` when it POSTs create-subscription. If the deposit
       * invoice declines (insufficient funds, fraud rule, etc.), the customer
       * previously saw a green Apple Pay checkmark followed by an error toast — a
       * deeply confusing UX that made it look like Defera had taken the money and
       * then failed. Now the wallet sheet matches the truth.
       */
      keepLocked = (await processPaymentMethod({
        paymentMethodId: setupIntent?.payment_method,
        session,
        capitalIdempotencyKey,
        onChargeOutcome: (succeeded) => {
          if (sheetClosed) return;
          try {
            ev.complete(succeeded ? 'success' : 'fail');
          } catch (e) {
            /** Sheet already timed out (>30s) — Apple Pay has auto-closed. Safe to ignore. */
            console.warn('Apple Pay ev.complete after sheet closed (non-fatal)', e);
          }
          sheetClosed = true;
        },
      })) === KEEP_LOCKED;

      /** Defensive safety net: if processPaymentMethod somehow returned without ever
       *  invoking the callback (programming error), close the sheet so it doesn't time
       *  out for the user. We treat this as failure to surface the bug. */
      if (!sheetClosed) {
        try { ev.complete('fail'); } catch { /* ignore */ }
        sheetClosed = true;
      }
    } catch (err) {
      if (!sheetClosed) {
        try { ev.complete('fail'); } catch { /* sheet already closed */ }
        sheetClosed = true;
      }
      console.error('Apple Pay checkout error:', err);
      const rawMsg = err?.message || 'Something went wrong';
      const msg = isStripeConnectionError(rawMsg)
        ? 'We had trouble connecting to our payment processor. Please check your internet connection and try again. If it persists, contact us at defera.app/contact.'
        : (formatStripeErrorMessage(err) || rawMsg);
      toast.error(msg);
      onError?.(err);
    } finally {
      if (!keepLocked) {
        submitLockRef.current = false;
        setIsProcessing(false);
      }
      // Always release the sibling form, including on the cancel path — a
      // customer who dismisses the Apple Pay sheet must get the card form back.
      onApplePayActiveChange?.(false);
    }
  };

  /**
   * Build the Stripe PaymentRequest (Apple Pay / Google Pay) for Defera Capital only.
   * The sheet's displayed `total` matches the deposit shown in the UI; the actual
   * money movement happens server-side via the existing deposit invoice path —
   * Stripe explicitly supports this "delayed-charge" pattern for SetupIntent flows.
   *
   * `canMakePayment()` resolves null on browsers/devices without a wallet card, in
   * which case we never set state, so the button never renders and existing UX is
   * unchanged for non-Apple-Pay users.
   */
  const walletDepositRef = useRef(depositCents);
  walletDepositRef.current = depositCents;
  const walletEligible = checkoutVariant === 'capital' && depositCents >= 100;
  useEffect(() => {
    if (!stripe || typeof stripe.paymentRequest !== 'function' || !walletEligible) {
      setPaymentRequest(null);
      return undefined;
    }

    let cancelled = false;
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: WALLET_DEPOSIT_LABEL, amount: walletDepositRef.current },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.on('paymentmethod', (ev) => {
      const handler = applePayHandlerRef.current;
      if (handler) {
        handler(ev);
      } else {
        ev.complete('fail');
      }
    });

    pr.canMakePayment()
      .then((res) => {
        if (cancelled) return;
        setPaymentRequest(res ? pr : null);
      })
      .catch(() => {
        if (!cancelled) setPaymentRequest(null);
      });

    return () => {
      cancelled = true;
    };
  }, [stripe, walletEligible]);

  // The wallet sheet quotes the deposit being charged. The button keeps the
  // request it mounted with (react-stripe-js won't swap it), so when Miles,
  // credit or the deposit choice move the deposit, the amount is updated in
  // place rather than on a new request the button never shows.
  useEffect(() => {
    if (!paymentRequest || !(depositCents >= 100)) return;
    try {
      paymentRequest.update({ total: { label: WALLET_DEPOSIT_LABEL, amount: depositCents } });
    } catch {
      /* the sheet is open: Stripe refuses updates until it closes */
    }
  }, [paymentRequest, depositCents]);

  if (loadingMethods) {
    // As a button-only island there is another form directly below us; a
    // spinner here would read as that form loading. Stay invisible instead.
    if (applePayOnly) return null;
    return (
      <>
        {/* The Miles card shows while the saved cards load: Miles that pay the whole trip need no card. */}
        {paymentMethodsTop && <div className="mb-4">{paymentMethodsTop}</div>}
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-white/60" />
        </div>
      </>
    );
  }

  const brandLabel = (b) => (b ? String(b).charAt(0).toUpperCase() + String(b).slice(1).toLowerCase() : 'Card');

  /**
   * Button-only island above the NMI card form. Renders nothing when the
   * device has no wallet card — `canMakePayment()` resolved null — so a
   * customer without Apple Pay sees the NMI form alone, unchanged.
   */
  if (applePayOnly) {
    if (!paymentRequest) return null;
    return (
      <div className="mb-6" data-testid="apple-pay-express">
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
            style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '48px' } },
          }}
        />
        <div className="flex items-center gap-3 mt-4" aria-hidden="true">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/40 text-[10px] uppercase tracking-[0.2em]">Or pay with card</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Payment method</p>
        {/* Set aside while a payment is in flight: changing the Miles then
            wouldn't change what that request spends. */}
        {paymentMethodsTop && (
          <div
            className={`mb-4 transition-opacity ${isProcessing ? 'opacity-60 pointer-events-none' : ''}`}
            inert={isProcessing ? '' : undefined}
            aria-busy={isProcessing || undefined}
          >
            {paymentMethodsTop}
          </div>
        )}
        {paymentRequest && (
          <div className="mb-5" data-testid="apple-pay-section">
            <PaymentRequestButtonElement
              options={{
                paymentRequest,
                style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '48px' } },
              }}
            />
            <div className="flex items-center gap-3 mt-4" aria-hidden="true">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-[10px] uppercase tracking-[0.2em]">Or pay with card</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          </div>
        )}
        {paymentMethods.length > 0 && !useNewCard ? (
          <div className="space-y-2.5">
            {paymentMethods.map((pm) => (
              <button
                key={pm.id}
                type="button"
                onClick={() => setSelectedCardId(pm.id)}
                className={`${paymentCardGradient.cardRowBase} ${
                  selectedCardId === pm.id ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected
                }`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedCardId === pm.id ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected
                }`}>
                  <CreditCard className={`w-5 h-5 ${selectedCardId === pm.id ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
                </div>
                <div className="flex-1 text-left">
                  <span className="font-medium text-white">{brandLabel(pm.brand)}</span>
                  <span className="text-white/50 font-mono text-sm ml-2">•••• {pm.last4}</span>
                </div>
                {selectedCardId === pm.id && (
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${paymentCardGradient.checkBadge}`}>
                    <Check className={`w-3.5 h-3.5 ${paymentCardGradient.checkIcon}`} strokeWidth={2.5} />
                  </div>
                )}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setUseNewCard(true); setSelectedCardId(null); }}
              className={paymentCardGradient.addNewCard}
            >
              <div className={paymentCardGradient.addNewCardIcon}>
                <CreditCard className="w-5 h-5 text-white/40" />
              </div>
              Add new card
            </button>
          </div>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className={paymentCardGradient.cardElementWrapper}>
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            {paymentMethods.length > 0 && (
              <button
                type="button"
                onClick={() => { setUseNewCard(false); setSelectedCardId(paymentMethods[0]?.id); }}
                className={paymentCardGradient.useSavedCardLink}
              >
                ← Use saved card instead
              </button>
            )}
          </div>
        )}
      </div>

      {renderBetween && (
        <div className="mt-8 pt-6">{renderBetween}</div>
      )}

      <Button
        type="button"
        onClick={handleConfirm}
        disabled={
          disabled ||
          !termsAccepted ||
          isProcessing ||
          (useNewCard && (!stripe || !elements)) ||
          (sliceInstallmentsMode && checkoutVariant === 'capital' && (!installmentCount || !depositCents))
        }
        className="w-full bg-green-400 hover:bg-green-400/90 text-black font-semibold py-7"
        size="lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Confirming...
          </>
        ) : (
          <>
            <Zap className="w-4 h-4 mr-2" />
            Confirm Booking
          </>
        )}
      </Button>

      <CheckoutPaymentTrustFooter className="mt-6" />
    </div>
  );
}

export default function DeferaZeroCheckoutForm(props) {
  if (!stripePromise) return null;
  return (
    <Elements stripe={stripePromise}>
      <DeferaZeroFormInner {...props} />
    </Elements>
  );
}
